import { eachDayOfInterval, isSameWeek } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';

import type {
  IAgentRaw,
  IClientContract,
  IClientContractRaw,
  IContractDetailsRaw,
  IContractRaw,
  IContractRequirementRaw,
  IResignationPendingRaw,
  IRestermReasonRaw,
  ITimeOffRaw,
  IUnmetContractRequirementRaw,
} from '@/api/types';
import type {
  AgentStatus,
  IAgent,
  IContract,
  IContractDetails,
  IContractRequirement,
  IContractSchedules,
  IResignationPending,
  IRestermReason,
  IScheduleSummary,
  ITimeOff,
  IUnmetContractRequirement,
} from '@/store/types';

import {
  calculateContractState,
  calculateDeadlineInAdvance,
  calculateDeadlineInFinal,
  calculateUnmetBlocks,
  getWorkSchedules,
  convertBlockToDate,
  dateWithTimeZone,
  formatDate,
  formatDateToUtc,
  getBlockFromDate,
  getContractStatusCode,
  getContractStatusLabel,
  getResignSurveyQuestionFromContractsList,
  sortAgents,
  sortUnmetContractRequirementsByDeadline,
  transformResTermTypeLabelFromPayload,
} from '@/helpers';
import { MONTH_DAY_HOUR_FORMAT, MONTH_DAY_YEAR_FORMAT } from '@/config/variables';
import { RestermReasonType } from '@/store/enums';
import { IGNORED_AGENT_STATUSES } from '@/constants/client';

const transformContracts = ({
  contracts,
  contractsPending,
}: {
  contracts: IContractRaw[];
  contractsPending: IResignationPending[];
}): IContract[] => {
  return contracts
    .sort((a, b) => (a.contractAssnStartBlock > b.contractAssnStartBlock ? 1 : -1))
    .map(contract => {
      // there might be more resigns pending, but only if we make them by accident on the front-end side
      const resignPending = contractsPending.find(item => item.agentContractId === contract.contractId);
      const statusCode = getContractStatusCode(contract, resignPending);
      const resTermProcessedOnAsString = contract.resTermProcessedOn
        ? formatDate(dateWithTimeZone(new Date(contract.resTermProcessedOn), contract.clientTZ), MONTH_DAY_YEAR_FORMAT)
        : '';
      return {
        ...contract,
        displayName: contract.gigName,
        resignPending,
        statusCode,
        statusLabel: getContractStatusLabel(statusCode, resTermProcessedOnAsString),
        startBlockAsString: formatDate(
          convertBlockToDate(contract.contractAssnStartBlock, contract.clientTZ),
          MONTH_DAY_YEAR_FORMAT,
        ),
        endBlockAsString: contract.contractAssnEndBlock
          ? formatDate(convertBlockToDate(contract.contractAssnEndBlock, contract.clientTZ), MONTH_DAY_YEAR_FORMAT)
          : '',
        resTermEffectiveBlockAsString: resignPending?.effectiveBlock
          ? formatDate(
              fromZonedTime(convertBlockToDate(resignPending?.effectiveBlock, contract.clientTZ), contract.clientTZ),
              MONTH_DAY_YEAR_FORMAT,
            )
          : '-',
        resTermReasonLabel: contract.resTermReasonLabel || '-',
        resTermProcessedOnAsString: resTermProcessedOnAsString,
        resTermTypeLabel: transformResTermTypeLabelFromPayload(contract.resTermTypeLabel),
        isCurrentContract:
          contract.contractAssnEndBlock === 0 ||
          contract.contractAssnEndBlock > getBlockFromDate(formatDateToUtc(new Date())), // UTC date on purpose
      };
    });
};

const transformClientContracts = ({ clientContracts }: { clientContracts: IClientContractRaw[] }): IClientContract[] =>
  clientContracts.map(item => ({
    agentPositionId: item.agentPositionId,
    clientId: item.clientId,
    contractId: item.agentContractId,
    contractName: item.agentContractName,
    displayName: item.gigName,
    endBlock: item.endBlock,
    isActive: item.isActive,
    clientTimezone: item.clientTimezone,
    clientName: item.clientName,
    durationTypeId: item.durationTypeId,
    jobId: item.agentPositionId,
    jobName: item.agentPositionName,
    snapshotBlock: item.snapshotBlock,
    startBlock: item.startBlock,
    weekStartBlock: item.weekStartBlock,
  }));

const transformRestremReasons = (
  restremReasons: IRestermReasonRaw[],
  restermReasonType?: RestermReasonType,
): IRestermReason[] =>
  restremReasons
    .filter(reason => (typeof restermReasonType === 'number' ? reason.type === restermReasonType : true))
    .sort((a, b) => (a.label > b.label ? 1 : -1))
    .map(reason => ({
      ...reason,
      value: reason.id,
    }));

const transformResignPendingContracts = (contracts: IResignationPendingRaw[]): IResignationPending[] =>
  contracts.map(contract => ({
    ...contract,
    survey: {
      2: getResignSurveyQuestionFromContractsList(2, contract.survey),
      3: getResignSurveyQuestionFromContractsList(3, contract.survey),
      6: getResignSurveyQuestionFromContractsList(6, contract.survey),
      7: getResignSurveyQuestionFromContractsList(7, contract.survey),
      8: getResignSurveyQuestionFromContractsList(8, contract.survey),
      9: getResignSurveyQuestionFromContractsList(9, contract.survey),
      10: getResignSurveyQuestionFromContractsList(10, contract.survey),
      13: getResignSurveyQuestionFromContractsList(13, contract.survey),
      14: getResignSurveyQuestionFromContractsList(14, contract.survey),
      15: getResignSurveyQuestionFromContractsList(15, contract.survey),
      16: getResignSurveyQuestionFromContractsList(16, contract.survey),
    },
  }));

const transformTimeOffsList = (timeOffsArray: ITimeOffRaw[], timeZone?: string): ITimeOff[] => {
  if (!timeZone) return [];

  const allTimeOffsSeparatedByWeek = timeOffsArray.map(timeOff => {
    const timeOffStartDateAsDate = dateWithTimeZone(new Date(timeOff.startDate), timeZone);
    const timeOffEndDateAsDate = dateWithTimeZone(new Date(timeOff.endDate), timeZone);
    const options: { weekStartsOn: 1 } = { weekStartsOn: 1 };

    if (isSameWeek(timeOffEndDateAsDate, timeOffStartDateAsDate, options)) {
      return [
        {
          ...timeOff,
          startDateWithTz: dateWithTimeZone(new Date(timeOff.startDate), timeZone).toString(),
          endDateWithTz: dateWithTimeZone(new Date(timeOff.endDate), timeZone).toString(),
        },
      ];
    }

    const timeOffDaysArray = eachDayOfInterval({ start: timeOffStartDateAsDate, end: timeOffEndDateAsDate });

    const timeOffSeparatedByWeek = timeOffDaysArray.reduce<{ [key: string]: string | any }>(
      (accumulator, currentTimeOffDay, index) => {
        // no previous weeks startDate, assign one and continue with the loop
        if (!accumulator.previousWeekday) {
          return {
            ...accumulator,
            grouppedWeekdays: [currentTimeOffDay],
            previousWeekday: currentTimeOffDay,
          };
        }

        if (!isSameWeek(accumulator.previousWeekday, currentTimeOffDay, { weekStartsOn: 1 })) {
          if (index === timeOffDaysArray.length - 1) {
            return {
              total: [...accumulator.total, accumulator.grouppedWeekdays, [currentTimeOffDay]],
              grouppedWeekdays: null,
              previousWeekday: null,
            };
          } else {
            return {
              total: [...accumulator.total, accumulator.grouppedWeekdays],
              grouppedWeekdays: [currentTimeOffDay],
              previousWeekday: currentTimeOffDay,
            };
          }
        } else {
          if (index === timeOffDaysArray.length - 1) {
            return {
              total: [...accumulator.total, [...accumulator.grouppedWeekdays, currentTimeOffDay]],
              grouppedWeekdays: null,
              previousWeekday: null,
            };
          } else {
            return {
              total: [...accumulator.total],
              grouppedWeekdays: [...accumulator.grouppedWeekdays, currentTimeOffDay],
              previousWeekday: currentTimeOffDay,
            };
          }
        }
      },
      { total: [], grouppedWeekdays: [], previousWeekday: null },
    );

    return timeOffSeparatedByWeek.total.map((timeOffArray: Date[]) => {
      const startDate = timeOffArray[0];
      const endDate = timeOffArray[timeOffArray.length - 1];

      return {
        ...timeOff,
        startDate: startDate.toString(),
        startDateWithTz: dateWithTimeZone(startDate, timeZone).toString(),
        endDate: endDate.toString(),
        endDateWithTz: dateWithTimeZone(endDate, timeZone).toString(),
      };
    });
  });

  return allTimeOffsSeparatedByWeek.flat();
};

const transformContractRequirements = (
  contractRequirements: IContractRequirementRaw[],
  timeZone?: string,
): IContractRequirement[] => {
  if (!timeZone) return [];

  return contractRequirements.map(contract => ({
    ...contract,
    contractRequirements: contract.contractRequirements.map(item => ({
      ...item,
      workSchedules: getWorkSchedules(item.contractRequirementBlocks, timeZone, contract.clientSummary.clientTimezone),
    })),
  }));
};

const transformContractRequirementsUnmet = (
  contractRequirementsUnmet: IUnmetContractRequirementRaw[],
): IUnmetContractRequirement[] =>
  contractRequirementsUnmet
    .map(contract => {
      const deadlineInAdvance = calculateDeadlineInAdvance(contract.offset, contract.weekId);
      const deadlineInFinal = calculateDeadlineInFinal(contract.weekId);

      return {
        ...contract,
        unmet: calculateUnmetBlocks(contract),
        requirementState: calculateContractState(contract),
        deadlineAdvance: deadlineInAdvance.toISOString(),
        deadlineAdvanceFormatted: formatDate(deadlineInAdvance, MONTH_DAY_HOUR_FORMAT),
        deadlineFinal: deadlineInFinal.toISOString(),
        deadlineFinalFormatted: formatDate(calculateDeadlineInFinal(contract.weekId), MONTH_DAY_HOUR_FORMAT),
      };
    })
    .sort(sortUnmetContractRequirementsByDeadline);

const transformContractSchedulesFromRequirements = (requirements: IContractRequirement[]): IContractSchedules =>
  requirements.reduce<IContractSchedules>((acc, item) => {
    acc[item.contractId] = acc[item.contractId] ?? [];
    const schedules = item.contractRequirements.flatMap(req =>
      req.contractRequirementScheduleSummary.map(schedule => schedule),
    );
    // filter unique schedules
    schedules.forEach(schedule => {
      const doesExist = acc[item.contractId].some(existSchedule => existSchedule.scheduleId === schedule.scheduleId);
      if (!doesExist) acc[item.contractId].push(schedule);
    });
    return acc;
  }, {});

const transformContractSchedules = (contractDetails: IContractDetailsRaw): IScheduleSummary[] => {
  const uniqueSchedules: IScheduleSummary[] = [];
  contractDetails.schedules.forEach(schedule => {
    const doesExist = uniqueSchedules.some(existSchedule => existSchedule.scheduleId === schedule.scheduleId);
    if (!doesExist) uniqueSchedules.push(schedule);
  });
  return uniqueSchedules;
};

const transformContractDetails = (contractDetailsRaw: IContractDetailsRaw): IContractDetails => {
  return {
    contractId: contractDetailsRaw.agentContractId,
    contractName: contractDetailsRaw.agentContractName,
    schedules: contractDetailsRaw.schedules,
    clientName: contractDetailsRaw.clientName,
    durationTypeId: contractDetailsRaw.durationTypeId,
    jobId: contractDetailsRaw.agentPositionId,
    jobName: contractDetailsRaw.agentPositionName,
    snapshotBlock: contractDetailsRaw.snapshotBlock,
    startBlock: contractDetailsRaw.startBlock,
    endBlock: contractDetailsRaw.endBlock,
    isActive: contractDetailsRaw.isActive,
    clientTimezone: contractDetailsRaw.clientTimezone,
    clientId: contractDetailsRaw.clientId,
    fullName: contractDetailsRaw.fullName,
    weekStartBlock: contractDetailsRaw.weekStartBlock,
  };
};

const _bitmaskOr = (value: number, bitmask: 1 | 2 | 4 | 8 | 16 | 32) => ((value >> Math.log2(bitmask)) & 1) === 1;

export const getAgentStatuses = (rawAgent: IAgentRaw): AgentStatus[] => {
  const statuses: AgentStatus[] = [];
  if (rawAgent.resTermStatus) {
    if (_bitmaskOr(rawAgent.resTermStatus, 1)) {
      statuses.push('active');
    }
    if (_bitmaskOr(rawAgent.resTermStatus, 2)) {
      statuses.push('resigned');
    }
    if (_bitmaskOr(rawAgent.resTermStatus, 4)) {
      statuses.push('suspended');
    }
    if (_bitmaskOr(rawAgent.resTermStatus, 8)) {
      statuses.push('removed');
    }
    if (_bitmaskOr(rawAgent.resTermStatus, 16)) {
      statuses.push('reinstated');
    }
    if (_bitmaskOr(rawAgent.resTermStatus, 32)) {
      statuses.push('ended');
    }
  }
  return statuses;
};

const transformAgents = (rawAgents: IAgentRaw[], restermReasons: IRestermReason[]): IAgent[] => {
  //combine agents by Id
  const agentsById = rawAgents.reduce<Record<number, IAgentRaw[]>>((acc, rawAgent) => {
    if (acc[rawAgent.userId]) acc[rawAgent.userId].push(rawAgent);
    else acc[rawAgent.userId] = [rawAgent];
    return acc;
  }, {});
  return Object.values(agentsById)
    .flatMap(arr =>
      arr.sort(sortAgents).map<IAgent>(rawAgent => ({
        ...rawAgent,
        agentStatuses: getAgentStatuses(rawAgent),
        restermReasonLabel: restermReasons.find(item => item.value === rawAgent.resTermReasonId)?.label || null,
        type: rawAgent.contractAssnEndBlock === 0 ? 'current' : 'history',
      })),
    )
    .sort(sortAgents)
    .filter(agent => !agent.agentStatuses.some(status => IGNORED_AGENT_STATUSES.includes(status)));
};

export {
  transformAgents,
  transformContracts,
  transformClientContracts,
  transformRestremReasons,
  transformResignPendingContracts,
  transformTimeOffsList,
  transformContractRequirements,
  transformContractRequirementsUnmet,
  transformContractSchedules,
  transformContractSchedulesFromRequirements,
  transformContractDetails,
};
